import React from "react";
import { Badge, Button, Card, Col, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ButtonLoading } from "go-form";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { ReactComponent as CalendarSVG } from "go-core/images/svg/calendar.svg";
import { ReactComponent as InvoiceTypeSVG } from "go-core/images/svg/file.svg";
import { InvoiceApi } from "../../../../../../../../../services/Api/Organization/types";
import { useKSeFStatusRequests } from "../../../hooks/useKSeFStatusRequests";
import { useCreateOfflineInvoiceReturn } from "../../../utils";

interface Props {
	invoice?: InvoiceApi;
	refetchingInvoiceInProgress?: boolean;
	fetchInvoice: () => Promise<InvoiceApi | null>;
	KSeFInfoCardOfflineInvoiceUtils: useCreateOfflineInvoiceReturn;
}

export const KSeFInfoCard = ({
	invoice,
	fetchInvoice,
	refetchingInvoiceInProgress,
	KSeFInfoCardOfflineInvoiceUtils,
}: Props) => {
	const { t } = useTranslation();
	const ksefData = invoice?.ksef_data;
	const { manageInvoicePreviewRefetching, refetchingInvoiceInProgress: loading } = useKSeFStatusRequests(
		fetchInvoice,
		invoice?.id,
		invoice?.settings.ksef_token_exists || invoice?.settings.ksef_auth_data_exists
	);
	const isLoading = Boolean(refetchingInvoiceInProgress || loading);
	const { KSeFServiceUnavailable, createOfflineInvoiceHandler, offlineCreationLoading } =
		KSeFInfoCardOfflineInvoiceUtils;

	return (
		<Col xs={12}>
			<Card>
				<Card.Body>
					{(Boolean(ksefData) || (!ksefData && isLoading)) && (
						<>
							<div className="d-flex align-items-start column-gap-3">
								<Card.Title as="h5">{t("modules.invoice.header.ksef_info.title")}</Card.Title>
								{ksefData?.environment_type === "TEST" && (
									<Badge bg="warning" text="dark">
										Test
									</Badge>
								)}
								{invoice?.ksef_data?.online === false && <Badge bg="danger">Offline</Badge>}
							</div>
							{isLoading && (
								<div className="d-flex align-items-center column-gap-1 mb-3 ms-1">
									<Spinner size="sm" variant="primary" />
									<p className="mb-0">
										{t("modules.invoice.action.sending_to_ksef_in_progress.title")}
									</p>
								</div>
							)}
						</>
					)}
					{ksefData && (
						<Row>
							<Col md={3} className="card-body-item">
								<CalendarSVG />
								<div className="item-info">
									<span className="label">{t("lib:common.word.created_at")}</span>
									<span>{FormatDate(ksefData.created_at)}</span>
								</div>
							</Col>
							<Col md={3} className="card-body-item">
								<InvoiceTypeSVG />
								<div className="item-info">
									<span className="label">{t("lib:common.word.status")}</span>
									<span>
										{t(`enums.common.status.${ksefData.status}`, { ns: ["lib", "translation"] })}
									</span>
								</div>
							</Col>
							{ksefData.number && (
								<Col md={3} className="card-body-item">
									<InvoiceTypeSVG />
									<div className="item-info">
										<span className="label">{t("modules.invoice.field.ksef_number.title")}</span>
										{ksefData.verification_qr_code_url ? (
											<Link
												to={{ pathname: ksefData.verification_qr_code_url }}
												target="_blank"
												rel="noopener noreferrer"
											>
												{ksefData.number}
											</Link>
										) : (
											ksefData.number
										)}
									</div>
								</Col>
							)}
							{ksefData.last_check_at && (
								<Col md={3} className="card-body-item">
									<CalendarSVG />
									<div className="item-info">
										<span className="label">{t("modules.invoice.field.last_check_at.title")}</span>
										<span>{FormatDate(ksefData.last_check_at)}</span>
									</div>
								</Col>
							)}
							{ksefData.error && (
								<Col md={6}>
									<div className="card-body-item mb-0">
										<InvoiceTypeSVG />
										<div className="item-info">
											<span className="label">{t("modules.invoice.field.error.title")}</span>
											<p className="text-break">{ksefData.error}</p>
										</div>
									</div>
									<div className="d-flex align-items-center column-gap-2">
										<Button onClick={manageInvoicePreviewRefetching}>
											{t("lib:common.action.send_again")}
										</Button>
										{KSeFServiceUnavailable && (
											<ButtonLoading
												loading={offlineCreationLoading}
												onClick={createOfflineInvoiceHandler}
											>
												{t("modules.invoice.action.create_invoice_in_offline_mode.title")}
											</ButtonLoading>
										)}
										{ksefData.help_url && (
											<Button
												as="a"
												href={ksefData.help_url}
												target="_blank"
												rel="noopener noreferrer"
											>
												{t("modules.invoice.field.documentation.title")}
											</Button>
										)}
									</div>
								</Col>
							)}
						</Row>
					)}
				</Card.Body>
			</Card>
		</Col>
	);
};

// t("enums.common.status.PENDING")
